import { nanoid } from "nanoid"
import { fetchResolvedJobShopData } from "@/composables/useJobShopData"
import { pickPasswordGateBranding } from "@/utils/passwordGateBranding"
import Logger from "@/utils/Logger"

/**
 * The branding for a locked JobShop's password form (JET-2155).
 *
 * The JobShop's identity comes from the request headers 000.resolve-domain.ts
 * injected, read server-side, not from the header store: on a locked render
 * the store stays empty so the JobShop id never reaches the payload
 * (plugins/header-store.server.ts).
 *
 * Reads the same cached settings as useJobShopData (fetchResolvedJobShopData:
 * one cache entry, not a second fetch), but under its own useAsyncData key, so only the
 * allow-listed branding (utils/passwordGateBranding.ts) reaches the payload.
 * The full settings stay on the server.
 *
 * Never throws: a JobShop whose settings cannot be read still gets its password
 * form, just with the generic look. Null in that case.
 */
export async function usePasswordGateBranding() {
  const { $api } = useNuxtApp()
  const headers = useRequestHeaders(["x-tenant-id", "x-jobshop-id", "accept-language"])
  const tenantId = headers["x-tenant-id"] ?? ""
  const jobShopId = headers["x-jobshop-id"] ?? ""
  const acceptLanguage = headers["accept-language"] ?? ""
  const { dataCacheTtl, dataCacheStaleTtl } = useRuntimeConfig()
  const requestId = nanoid()

  const { data } = await useAsyncData("passwordGateBranding", async () => {
    try {
      const jobShop = await fetchResolvedJobShopData({ $api, tenantId, jobShopId, acceptLanguage, dataCacheTtl, dataCacheStaleTtl, requestId })

      return pickPasswordGateBranding(jobShop)
    }
    catch (error) {
      Logger(requestId).warn(`Could not read the branding for the password form of jobShopId "${jobShopId}". Showing it unbranded.`, error)
      return null
    }
  })

  return data
}
